import { useRef, useState } from 'react'
import { ExternalLink, FileText, Loader2, Plus, RefreshCw, Trash2, Upload } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import {
  useDealDocuments,
  useDeleteDocument,
  useDocumentTemplates,
  useGenerateDocument,
  usePatchDocument,
  useUploadDocumentFile,
} from '@/features/documents/hooks'
import { documentRenderUrl } from '@/features/documents/api'
import {
  DOCUMENT_STATUS_LABELS,
  DOCUMENT_STATUS_VARIANT,
  type DealDocument,
  type DocumentStatus,
} from '@/types/documents'

const STATUS_ORDER: DocumentStatus[] = ['pending', 'generated', 'sent', 'signed', 'completed']

const formatBytes = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/**
 * DocumentsCard is the sales-paperwork checklist on the deal detail page.
 * Pending rows are stage requirements created automatically when the deal
 * entered its stage; generating renders the template with the deal's current
 * data and keeps every generated version as immutable history.
 */
export function DocumentsCard({ dealId }: { dealId: string }) {
  const { data: docs = [], isLoading } = useDealDocuments(dealId)
  const { data: templates = [] } = useDocumentTemplates()
  const generateMutation = useGenerateDocument(dealId)
  const patchMutation = usePatchDocument(dealId)
  const uploadMutation = useUploadDocumentFile(dealId)
  const deleteMutation = useDeleteDocument(dealId)

  const [generateOpen, setGenerateOpen] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState<string>('')
  const [uploadingId, setUploadingId] = useState<string>('')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const uploadTargetRef = useRef<string>('')

  const openGenerate = (templateId?: string) => {
    setSelectedTemplate(templateId ?? '')
    setGenerateOpen(true)
  }

  const handleGenerate = () => {
    if (!selectedTemplate) return
    generateMutation.mutate(selectedTemplate, { onSuccess: () => setGenerateOpen(false) })
  }

  const openPrintView = (doc: DealDocument) => {
    window.open(documentRenderUrl(doc.id), '_blank', 'noopener')
  }

  const handleUploadClick = (docId: string) => {
    uploadTargetRef.current = docId
    setUploadingId(docId)
    fileInputRef.current?.click()
  }

  const handleFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (file && uploadTargetRef.current) {
      uploadMutation.mutate(
        { id: uploadTargetRef.current, file },
        { onSettled: () => setUploadingId('') },
      )
    } else {
      setUploadingId('')
    }
  }

  // Latest version first per template key, then by creation order.
  const sorted = [...docs].sort((a, b) =>
    a.template_key === b.template_key
      ? a.created_at.localeCompare(b.created_at)
      : a.template_key.localeCompare(b.template_key),
  )

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <div className="space-y-1.5">
          <CardTitle className="flex items-center gap-2 text-base">
            <FileText className="h-4 w-4" /> Sales Documents
          </CardTitle>
          <CardDescription>
            Required paperwork for this deal, created automatically as it moves through the
            pipeline. Generated documents are immutable snapshots — open, print, and attach the
            signed copy.
          </CardDescription>
        </div>
        <Button size="sm" onClick={() => openGenerate()}>
          <Plus className="mr-1.5 h-3.5 w-3.5" /> Generate
        </Button>
      </CardHeader>
      <CardContent>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          className="hidden"
          onChange={handleFileSelected}
        />
        {isLoading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading documents…
          </div>
        ) : sorted.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No documents yet. Stage requirements appear here automatically; use Generate to create
            one from a template.
          </p>
        ) : (
          <ul className="divide-y">
            {sorted.map((doc) => (
              <li key={doc.id} className="flex flex-wrap items-center gap-2 py-2.5">
                <Badge variant={DOCUMENT_STATUS_VARIANT[doc.status]}>
                  {DOCUMENT_STATUS_LABELS[doc.status]}
                </Badge>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{doc.template_name}</div>
                  {doc.notes && (
                    <div className="truncate text-xs text-muted-foreground">{doc.notes}</div>
                  )}
                </div>
                {doc.has_file && (
                  <span className="text-xs text-muted-foreground">
                    {formatBytes(doc.file_size)}
                  </span>
                )}
                <TooltipProvider delayDuration={200}>
                  <div className="flex items-center gap-1">
                    <Select
                      value={doc.status}
                      onValueChange={(value) =>
                        patchMutation.mutate({
                          id: doc.id,
                          body: { status: value as DocumentStatus },
                        })
                      }
                    >
                      <SelectTrigger className="h-8 w-[124px] text-xs" aria-label="Document status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_ORDER.map((s) => (
                          <SelectItem key={s} value={s}>
                            {DOCUMENT_STATUS_LABELS[s]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {doc.has_rendered_body && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 gap-1.5 px-2.5 text-xs"
                            aria-label={`View ${doc.template_name}`}
                            onClick={() => openPrintView(doc)}
                          >
                            <ExternalLink className="h-3.5 w-3.5" /> View
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Open generated document</TooltipContent>
                      </Tooltip>
                    )}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          aria-label={`Upload signed copy of ${doc.template_name}`}
                          onClick={() => handleUploadClick(doc.id)}
                        >
                          {uploadMutation.isPending && uploadingId === doc.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Upload className="h-3.5 w-3.5" />
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Attach signed copy</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          aria-label={`Re-generate ${doc.template_name}`}
                          disabled={!doc.template_id}
                          onClick={() => openGenerate(doc.template_id ?? undefined)}
                        >
                          <RefreshCw className="h-3.5 w-3.5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Re-generate from template</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive"
                          aria-label={`Delete ${doc.template_name}`}
                          onClick={() => deleteMutation.mutate(doc.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Delete document</TooltipContent>
                    </Tooltip>
                  </div>
                </TooltipProvider>
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      <Dialog open={generateOpen} onOpenChange={setGenerateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Generate document</DialogTitle>
            <DialogDescription>
              Renders the template with this deal's current customer, boat, and pricing data. Any
              previously generated versions are kept.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="document-template">Template</Label>
            <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
              <SelectTrigger id="document-template">
                <SelectValue placeholder="Choose a template…" />
              </SelectTrigger>
              <SelectContent>
                {templates.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name}
                    {t.required_stage ? ` (${t.required_stage})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGenerateOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleGenerate} disabled={!selectedTemplate || generateMutation.isPending}>
              {generateMutation.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              Generate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}
